import { useLazyQuery } from '@apollo/react-hooks'
import { H1 } from '@berlitz/globals'
import Spacer from '@berlitz/spacer'
import BodySpinner from '@components/BodySpinner'
import Error from '@components/Error'
import Fileupload from '@components/Fileupload'
import MenuTab, { MenuTabItem } from '@components/MenuTab'
import PageTitle from '@components/PageTitle'
import PageWithRightSidebarTpl from '@components/PageWithRIghtSidebarTpl'
import Radio from '@components/Radio'
import RouteLink from '@components/RouteLink'
import { GetAchievementsResponse } from '@interfaces/Achievements'
import { StoreProps } from '@interfaces/StoreState'
import { GET_ACHIEVEMENTS } from '@queries/achievements'
import { FEATURE_TYPE } from '@utils/constants'
import { mainNav, subNav } from '@utils/stubdata/settings.json'
import * as React from 'react'
import { useEffect, useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { LayoutProps, MainNavProps, SubNavProps } from './interface'
import { Header, MainNav, SubNav, Wrapper } from './style'
import useCTPermission from '@hooks/useCTPermission'
import StudentProfile from '@classes/StudentProfile'
import Alert from '@components/Alert'

type ISubNav = {
  id: string
  label: string
  url: string
}[][]

const SUB_NAV_LABELS = {
  CERTIFICATES: 'Certificates',
} as const

const Layout: React.FC<LayoutProps> = ({
  title,
  children,
  activeMainNav,
  activeSubNav,
  activeSubNavGroup,
  featureType = '',
}) => {
  const intl = useIntl()
  const router = useRouter()
  const [modalOpen, setModalOpen] = useState(false)
  const [usedSubNav, setUsedSubNav] = useState<ISubNav>()
  const onModalClose = () => setModalOpen(false)
  const { profile, studentProfile } = useSelector(({ userProfile }: StoreProps) => {
    return {
      profile: userProfile.info,
      studentProfile: new StudentProfile(userProfile.info.StudentProfile.StudentProfileInfo),
    }
  })

  // Checking current page is the certificates page
  const isCertificatesPage = router.pathname === '/settings/certificates'

  const mainNavbar = useMemo(() => {
    let navbar = mainNav
    if (featureType?.indexOf(FEATURE_TYPE.JAPAN_FLEX) >= 0 || featureType?.indexOf(FEATURE_TYPE.ON_DEMAND) >= 0) {
      navbar = mainNav.filter(({ value }) => value === 'Account')
    }
    // Hide Payment tab if user is not enrolled in 2B or 2A & 2B programs
    if (!studentProfile?.SFId2B) {
      navbar = navbar.filter(({ value }) => value !== 'Payment')
    }
    return navbar
  }, [featureType, studentProfile?.SFId2B])
  const [getCertificates, { loading, data, called }] = useLazyQuery<GetAchievementsResponse>(GET_ACHIEVEMENTS)
  const [getLatestCert] = useLazyQuery<GetAchievementsResponse>(GET_ACHIEVEMENTS, {
    fetchPolicy: 'no-cache',
  })
  const [ctPermissionEnabled, setCtPermissionEnabled] = useState(false)

  const { getPermissionInStudentPrograms } = useCTPermission()

  useEffect(() => {
    const certVar = {
      variables: {
        studentProfileId: profile.SFId,
      },
    }
    const ctEnabled = getPermissionInStudentPrograms()
    setCtPermissionEnabled(ctEnabled)
    if (ctEnabled) {
      getCertificates(certVar)
      getLatestCert(certVar) // additional request to make sure we're not caching the certificates
    } else {
      const temp = JSON.parse(JSON.stringify(subNav)) as ISubNav
      temp[1] = temp[1].filter((item) => item.label !== SUB_NAV_LABELS.CERTIFICATES)
      if (studentProfile.OnlyOnDemand || studentProfile.has2BOrF2fStarProgramsOnly) {
        temp[1] = temp[1].filter((item) => !['Preferences', 'Learning profile'].includes(item.label))
      }
      setUsedSubNav(temp)
    }
  }, [])

  useEffect(() => {
    if (called && !loading && (!data || !data?.getAchievements?.data?.length)) {
      const temp = JSON.parse(JSON.stringify(subNav)) as ISubNav
      // Always hide Certificates tab when there are no certificates
      temp[1] = temp[1].filter((item) => item.label !== SUB_NAV_LABELS.CERTIFICATES)
      if (studentProfile.OnlyOnDemand || studentProfile.has2BOrF2fStarProgramsOnly) {
        temp[1] = temp[1].filter((item) => !['Preferences', 'Learning profile'].includes(item.label))
      }
      setUsedSubNav(temp)
    } else if (called && !loading) {
      const temp = JSON.parse(JSON.stringify(subNav)) as ISubNav
      if (studentProfile.OnlyOnDemand || studentProfile.has2BOrF2fStarProgramsOnly) {
        temp[1] = temp[1].filter((item) => !['Preferences', 'Learning profile'].includes(item.label))
      }
      setUsedSubNav(temp)
    }
  }, [called, loading, data])

  const childrenWithProps = React.Children.map(children, (child) => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { data })
    }
    return child
  })

  // Block access to certificates page when there are no certificates or CT permission is disabled
  // usedSubNav is used as initialization check - it's only set after permission check completes
  const shouldBlockCertificatesPage =
    isCertificatesPage &&
    usedSubNav &&
    (!ctPermissionEnabled || (called && !loading && (!data || !data?.getAchievements?.data?.length)))

  if (shouldBlockCertificatesPage) {
    return <Error statusCode={404} />
  }

  return (
    <>
      <PageTitle title={subNav[activeSubNavGroup][activeSubNav - 1]?.label} />
      {loading ? (
        <BodySpinner />
      ) : (
        <PageWithRightSidebarTpl
          body={
            <Wrapper>
              <Header>
                <H1 disableMargin>{title}</H1>
              </Header>

              <MainNav>
                <Radio.Group value={activeMainNav} onChange={() => {}} name="mainNav">
                  {mainNavbar.map(({ id, label, value, url }: MainNavProps) => (
                    <React.Fragment key={id}>
                      <Radio.Button value={value}>
                        <RouteLink key={url} path={url}>
                          {label ? <FormattedMessage id={label} defaultMessage={label} /> : label}
                        </RouteLink>
                      </Radio.Button>
                    </React.Fragment>
                  ))}
                </Radio.Group>
              </MainNav>
              <Spacer />

              <SubNav>
                <MenuTab activeTab={activeSubNav}>
                  {usedSubNav?.[activeSubNavGroup]?.map(({ id, label, url }: SubNavProps) => (
                    <MenuTabItem key={id} smallWidth>
                      <RouteLink key={url} path={url}>
                        {label ? <FormattedMessage id={label} defaultMessage={label} /> : label}
                      </RouteLink>
                    </MenuTabItem>
                  ))}
                </MenuTab>
              </SubNav>

              {activeMainNav === 'Learning' &&
                studentProfile?.has2Aand2BSubscriptions &&
                (activeSubNav === 1 || activeSubNav === 2) && (
                  <>
                    <Spacer size="md" />
                    <Alert alertType="error" dismissable={false}>
                      <FormattedMessage
                        id="Note: It is applicable for Flex, Standalone and BOD programs only."
                        defaultMessage="Note: It is applicable for Flex, Standalone and BOD programs only.
"
                      />
                    </Alert>
                    <Spacer size="md" />
                  </>
                )}

              <div>{childrenWithProps}</div>
              <Fileupload
                modalTitle={intl.formatMessage({ id: 'Upload files' })}
                open={modalOpen}
                onClose={onModalClose}
                center
              />
            </Wrapper>
          }
          sidebar={<div />}
          showSidebar={false}
        />
      )}
    </>
  )
}

export default Layout